import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "./context";
import { t } from "./i18n";
import { ArrowRight, MapPin, ShieldCheck, Sparkles } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/2155e805-d421-459a-a183-8bf5554e4515/images/cbb56e99c6519c55b1d22a31892c2838152e6b62d4083bf5f04b332e36ea8020.png";

export default function HomePage() {
  const { lang, API } = useApp();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/apartments?featured=true`).then(r => setFeatured(r.data)).catch(()=>{});
  }, [API]);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative" style={{ height: "85vh", minHeight: 560 }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay" />
        <div className="container-x relative h-full flex flex-col justify-end pb-16 md:pb-24 text-white fade-up">
          <span className="overline mb-6" style={{ color: 'rgba(255,255,255,0.85)' }} data-testid="hero-eyebrow">{t("hero.eyebrow", lang)}</span>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl leading-none tracking-tight max-w-3xl" data-testid="hero-title">
            {t("hero.title", lang)}
          </h1>
          <p className="mt-6 max-w-xl text-lg" style={{ color: 'rgba(255,255,255,0.92)' }}>
            {t("hero.sub", lang)}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/apartments" className="btn-primary" data-testid="hero-cta-view">
              {t("hero.cta_view", lang)} <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/contact" className="btn-outline" style={{ borderColor: '#fff', color: '#fff' }} data-testid="hero-cta-contact">
              {t("nav.contact", lang)}
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="section-pad">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="overline mb-3">{t("home.featured_eyebrow", lang)}</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="featured-title">{t("home.featured_title", lang)}</h2>
              <p className="mt-3 max-w-lg" style={{ color: 'var(--text-secondary)' }}>{t("home.featured_sub", lang)}</p>
            </div>
            <Link to="/apartments" className="btn-link self-start md:self-end" data-testid="featured-view-all">
              {t("listings.title", lang)} →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featured.map((apt, i) => (
              <Link to={`/apartments/${apt.slug}`} key={apt.id} className="listing-card group" data-testid={`featured-card-${apt.slug}`}>
                <div className="listing-img-wrap">
                  <img src={apt.images[0]} alt={apt.name} className="listing-img" />
                </div>
                <div className="pt-5">
                  <div className="overline">{lang === "en" ? apt.location_name_en : apt.location_name_sr}</div>
                  <h3 className="font-display text-2xl mt-2">{apt.name}</h3>
                  <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {lang === "en" ? apt.short_description_en : apt.short_description_sr}
                  </p>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span><span className="font-display text-2xl">€{apt.price_per_night}</span> <span style={{ color: 'var(--text-muted)' }}>{t("listings.night", lang)}</span></span>
                    <span style={{ color: 'var(--text-primary)' }} className="overline">{t("listings.view_details", lang)} →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIRECT */}
      <section className="section-pad" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="overline mb-3">{t("home.why_eyebrow", lang)}</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="why-title">{t("home.why_title", lang)}</h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12">
            {[ShieldCheck, MapPin, Sparkles].map((Icon, i) => {
              const item = t("home.why_items", lang) || [];
              const data = Array.isArray(item) ? item[i]?.[lang] : null;
              const key = data?.[0] || `why-${i}`;
              return (
                <div key={key} className="fade-up" style={{ animationDelay: `${i*120}ms` }}>
                  <Icon size={28} strokeWidth={1.5} style={{ color: 'var(--accent-primary)' }} />
                  <h3 className="font-display text-2xl mt-5">{data?.[0]}</h3>
                  <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>{data?.[1]}</p>
                </div>
              );
            })}          </div>
        </div>
      </section>
    </div>
  );
}
